import { Controller, useFormContext } from "react-hook-form";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Membership } from "@/types/system-setting/membership";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetIncomeCategorListQuery, useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetSalesTaxListQuery, useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import Combobox from "@/components/ui/common/combobox";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { TaxGroupType } from "@/types/enums";
import { useGetPriceMastersQuery } from "@/services/priceMasterApi";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import PriceMasterForm from "../../price_master/modal/form";
import { PriceMaster } from "@/types/system-setting/priceMaster";

const PriceDiscountTaxForm = () => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { data: incomeCategoryData } = useGetIncomeCategoryQuery({
    org_id: orgId, query: "status=active"
  });

  const { data: taxGroupData } = useGetTaxGroupQuery({
    query: "",
    org_id: orgId,
  });

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? taxGroupData?.data.filter((tg) => tg.type === TaxGroupType.Item).map((taxGroup) => ({
        label: taxGroup.name,
        value: String(taxGroup.id),
      }))
      : [];
  }, [taxGroupData]);

  const incomeCategoryOptions = useMemo(() => {
    return Array.isArray(incomeCategoryData?.data)
      ? incomeCategoryData?.data.map((incomeCategory) => ({
        label: incomeCategory.name,
        value: String(incomeCategory.id),
      }))
      : [];
  }, [incomeCategoryData]);

  // Fetch Price Master rules
  const { data: priceMasterData, isLoading: priceMasterLoading } = useGetPriceMastersQuery({
    query: "item_type=membership",
    org_id: orgId,
  });

  const priceMasterOptions = useMemo(() => {
    return priceMasterData && Array.isArray((priceMasterData as any).data)
      ? (priceMasterData as any).data.map((rule: any) => ({
          label: `${rule.rule_code} : ${rule.rule_name}`,
          subLabel: rule.rule_name,
          value: String(rule.id),
        }))
      : [];
  }, [priceMasterData]);

  // Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedRuleData, setSelectedRuleData] = useState<PriceMaster | null>(null);

  const handlePreview = (ruleId: number) => {
    const rule = (priceMasterData as any)?.data?.find((r: any) => r.id === ruleId);
    if (rule) {
      setSelectedRuleData(rule);
      setPreviewOpen(true);
    }
  };

  const {
    control,
    formState: { errors },
    register,
    setValue,
    getValues,
    trigger,
    watch,
  } = useFormContext<Membership>();


  const [autoRenewal, setAutoRenewal] = useState<boolean | undefined>(
    (getValues("auto_renewal") as boolean) || false
  );

  useEffect(() => {
    const subscription = watch((value) => {
      setAutoRenewal(value?.auto_renewal);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleDayInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof Membership
  ) => {
    const value = parseInt(e.target.value, 10);
    if (value >= 15) {
      setValue(field, 15);
    }
  };



  return (
    <div className="text-black space-y-4">
      <Card className="p-4">
        <h1 className="font-semibold text-[#2D374] text-xl">
          Price, Discounts, and Tax Details
        </h1>
        <div className="grid grid-cols-12 gap-6 items-start mt-4">
          <div className="col-span-12 lg:col-span-5 flex items-center gap-2">
            <Controller
              name="price_master_id"
              control={control}
              rules={{ required: "Required" }}
              render={({ field, fieldState: { error } }) => (
                <div className="flex-1">
                  <Combobox
                    list={priceMasterOptions}
                    defaultValue={priceMasterOptions.find((opt: any) => opt.value === String(field.value))?.label || ""}
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = priceMasterOptions.find((opt: any) => opt.label === selectedLabel);
                      field.onChange(selectedOption ? Number(selectedOption.value) : undefined);
                    }}
                    label="Price Master Rule"
                    disabled={priceMasterLoading}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </div>
              )}
            />
            <Controller
              name="price_master_id"
              control={control}
              render={({ field }) => (
                <>
                  {field.value && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-indigo-600 hover:bg-indigo-50 border border-indigo-100 shrink-0"
                      onClick={() => handlePreview(Number(field.value))}
                      title="Preview details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                </>
              )}
            />
          </div>

          <div className="col-span-12 lg:col-span-3">
            <Controller
              name="income_category_id"
              rules={{ required: "Required" }}
              control={control}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => (
                <div>
                  <Combobox
                    list={incomeCategoryOptions}
                    defaultValue={incomeCategoryOptions.find(option => option.value === value?.toString())?.label || ""}
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = incomeCategoryOptions.find(option => option.label === selectedLabel);
                      onChange(selectedOption ? Number(selectedOption.value) : undefined);
                    }}
                    label="Income Category"
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>
          
          <div className="col-span-12 lg:col-span-4">
            <Controller
              name="tax_group_id"
              rules={{ required: "Required" }}
              control={control}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { invalid, error },
              }) => (
                <div>
                  <Combobox
                    list={taxGroupOptions}
                    defaultValue={taxGroupOptions.find(option => option.value === value?.toString())?.label || ""}
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = taxGroupOptions.find(option => option.label === selectedLabel);
                      onChange(selectedOption ? Number(selectedOption.value) : undefined);
                    }}
                    label="Tax Group"
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>
        </div>
      </Card>


      <Card className="p-4">
      <h1 className="font-semibold text-[#2D374] text-xl">
        Renewal and Billing Cycle
      </h1>
      <div className="grid grid-cols-3 mt-3 mb-4">
        <Controller
          name="billing_cycle"
          rules={{ required: "Required" }}
          control={control}
          render={({
            field: { onChange, value, onBlur },
            fieldState: { invalid, error },
          }) => (
            <div>
              <Select
                onValueChange={(value) => {
                  onChange(value);
                }}
                value={value}
              >
                <SelectTrigger
                  name="billing_cycle"
                  floatingLabel="Billing Cycle"
                  text="*"
                >
                  <SelectValue placeholder="Select billing cycle" />
                </SelectTrigger>
                {invalid && (
                  <span className="text-destructive block !mt-[5px] text-[12px]">
                    {error?.message}
                  </span>
                )}
                <SelectContent>
                  <SelectItem value={"once"}>Once</SelectItem>
                  <SelectItem value={"month"}>Monthly</SelectItem>
                  <SelectItem value={"quarter"}>Quarterly</SelectItem>
                  <SelectItem value={"year"}>Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        />
      </div>
      <div className=" flex gap-6 items-center">
        <Controller
          name="auto_renewal"
          control={control}
          defaultValue={false}
          render={({
            field: { onChange, value, onBlur },
            fieldState: { invalid, error },
          }) => (
            <div className="flex py-0.5 items-center gap-2">
              <Checkbox
                checked={value}
                onCheckedChange={(e) => onChange(e)}
                aria-label="Auto Renewal?"
              />
              <Label className="">Auto Contract Renewal?</Label>
            </div>
          )}
        />
        {autoRenewal && (
          <div className="flex items-center gap-3 text-sm">
            <Label className="font-semibold ">
              Prolongation period<span className="text-red-500">*</span>
            </Label>
            <FloatingLabelInput
              id="prolongation_period"
              type="number"
              min={1}
              max={15}
              className="w-20 "
              {...register("prolongation_period", {
                required: "Required",
                valueAsNumber: true,
                max: {
                  value: 15,
                  message: `Prolongation Period must not be greater than 15`,
                },
              })}
              error={errors.prolongation_period?.message}
            />
          </div>
        )}
      </div>
      </Card>

      {/* Price Master Preview Modal */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="overflow-y-auto max-w-full sm:max-w-[700px] w-full p-0">
          <SheetHeader className="p-6 border-b bg-indigo-50/50">
            <SheetTitle className="text-xl font-bold text-indigo-900">
              Pricing Rule Preview
            </SheetTitle>
            <SheetDescription className="text-indigo-600">
              Viewing pre-configured pricing rule details
            </SheetDescription>
          </SheetHeader>
          
          <div className="p-6">
            {selectedRuleData && (
              <PriceMasterForm 
                isOpen={previewOpen} 
                setOpen={setPreviewOpen} 
                action="edit" 
                data={selectedRuleData} 
                readOnly={true}
                setData={() => {}}
                setAction={() => {}}
                refetch={() => {}}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default PriceDiscountTaxForm;